import { Redirect, router, Stack } from "expo-router";
import { observer } from "mobx-react";
import React from "react";
import { ScrollView } from "react-native";
import { Header } from "~/components/Header";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { ButtonCircle } from "~/components/V2/ButtonCircle";
import { StepTopNav } from "~/components/V2/StepTopNav";
import { BodyText } from "~/components/V2/Text/BodyText";
import { View } from "~/components/View";
import { useStandaloneBookingFlow } from "~/features/book-appointment/useStandaloneBookingFlow";
import { useAlert } from "~/hooks/useAlert";
import { useIsOptanonReady } from "~/hooks/useIsOptanonReady";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 as C } from "~/style/styleConstantsV2";

const AppLayout = observer(function AppLayout() {
  const store = useStore();
  const alert = useAlert();
  const shouldShowBackButton = router.canGoBack();
  const { clinicId } = useStandaloneBookingFlow();

  const hasAccessToken = !!store.authStore.accessToken;
  const isOptanonReady = useIsOptanonReady();

  // We have to check if user has an access token
  // We are not using isLoggedIn here because isLoggedIn checks if active user exists
  // and standalone booking flows allows new users to book appointments without an active user
  if (hasAccessToken === false && clinicId !== undefined) {
    return (
      <Redirect href={`/(standalone)/booking-login?clinicId=${clinicId}`} />
    );
  }

  return (
    <Screen withBottomInset withTopInset preventScroll>
      <Layout
        cardStyle={{
          borderTopRightRadius: 2,
          borderTopLeftRadius: 2,
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
        }}
      >
        {isOptanonReady && (
          <View flex>
            <StepTopNav shouldAnimateProgressBar>
              {shouldShowBackButton ? (
                <ButtonCircle
                  iconName="left-arrow"
                  size="small"
                  backgroundColor={C.colorGrey10}
                  onPress={() => {
                    router.back();
                  }}
                />
              ) : (
                <View style={{ width: 20, height: 20 }} />
              )}
              <View flex centerContent>
                <BodyText weightBold sizeExtraLarge colorBlack>
                  {intl.formatMessage({
                    id: "gOPXE6",
                    defaultMessage: "Book Clinic Visit",
                    description: "Title for booking a clinic visit",
                  })}
                </BodyText>
              </View>
              <ButtonCircle
                iconName="close"
                size="small"
                backgroundColor={C.colorGrey10}
                onPress={() => {
                  alert(
                    intl.formatMessage({
                      id: "7wz3H0",
                      defaultMessage: "Are you sure you want to logout?",
                      description: "Logout confirmation message",
                    }),
                    "",
                    [
                      {
                        text: intl.formatMessage({
                          id: "jJhhUG",
                          defaultMessage: "No, Cancel",
                          description: "Logout button text",
                        }),
                        style: "destructive",
                      },
                      {
                        text: intl.formatMessage({
                          id: "kIi12/",
                          defaultMessage: "Yes, Logout",
                          description: "Logout button text",
                        }),
                        style: "default",
                        onPress: () => {
                          // store.bookingStore.resetBooking();

                          store.authStore.logout(
                            `/(standalone)/booking-login?clinicId=${clinicId}`,
                          );
                        },
                      },
                    ],
                  );
                }}
              />
            </StepTopNav>
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                backgroundColor: C.colorWhite,
              }}
              style={{
                backgroundColor: C.colorWhite,
              }}
            >
              <Stack
                initialRouteName="index"
                screenOptions={{
                  header: (props) => <Header {...props} />,
                  freezeOnBlur: false,
                  animation: "slide_from_right",
                }}
              >
                <Stack.Screen
                  name="index"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="search-clinic"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="appointment-type"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="pet"
                  options={{
                    headerShown: false,
                  }}
                />

                <Stack.Screen
                  name="add-new-pet"
                  options={{
                    headerShown: false,
                  }}
                />

                <Stack.Screen
                  name="practitioners-preference"
                  options={{
                    headerShown: false,
                  }}
                />

                <Stack.Screen
                  name="select-practitioner"
                  options={{
                    headerShown: false,
                  }}
                />

                <Stack.Screen
                  name="date-and-time"
                  options={{
                    headerShown: false,
                  }}
                />

                <Stack.Screen
                  name="notes"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="add-client-details"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="add-existing-client-details"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="overview"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="success"
                  options={{
                    headerShown: false,
                  }}
                />
                <Stack.Screen
                  name="edit-contact-details"
                  options={{
                    headerShown: false,
                  }}
                />
              </Stack>
            </ScrollView>
          </View>
        )}
      </Layout>
    </Screen>
  );
});

export default AppLayout;
